import React, { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import BottomSheet from "./BottomSheet";

export interface FilterSheetOption {
  value: string;
  label: string;
}

export interface FilterSheetGroup {
  /** What this set narrows by — "Period", "Status". Names the section. */
  label: string;
  options: FilterSheetOption[];
  value: string;
  onChange: (value: string) => void;
  /**
   * The value that counts as not narrowed at all, if there is one. Only used
   * to mark the trigger while a filter is on: a phone has no room to show what
   * is picked, and a table quietly showing a third of its rows with nothing on
   * screen saying why is the one thing this layout could lose.
   */
  defaultValue?: string;
}

export interface FilterSheetProps {
  /** Every set the screen narrows by, in the order they narrow in. */
  groups: FilterSheetGroup[];
  /** The sheet's heading. */
  title?: string;
  className?: string;
}

/**
 * Every filter on a screen behind one word, opened as a bottom sheet.
 *
 * The phone's shape for a row of controls a wider screen can afford to leave
 * standing: a dropdown per filter takes a line of its own before the table has
 * been reached, and a tab strip holding four of them has to scroll sideways
 * behind a gesture nothing announces. One "Filter" beside the heading costs
 * nothing until it is asked for, and then the sheet has room for all of them
 * at once — see `BottomSheet`.
 *
 * Inside, the sets are folded rather than laid end to end. A sheet is only as
 * tall as the bottom half of a phone, and two sets spelled out in full scroll
 * inside it — which hides the second one behind the same unannounced gesture
 * the tab strip was replaced for. Folded, every set is on screen as a row that
 * says what it narrows by and what it is currently set to, and opening one is
 * a press. One at a time, so the row that opens is not pushed off the bottom
 * by the one above it.
 *
 * An unfolded set is a field of pills rather than a column of rows: the labels
 * are two or three words each, so a row apiece leaves most of the sheet empty
 * and pushes the set below it out of reach, while pills wrap and the whole
 * choice is taken in at once.
 *
 * Choices apply as they are made — there is no Apply to forget, and the table
 * behind answers as it is asked — so the sheet has no button of its own to
 * finish with. It is dismissed by pressing the screen outside it, which is
 * also what says nothing was left half-done.
 */
const FilterSheet: React.FC<FilterSheetProps> = ({
  groups,
  title = "Filters",
  className = "",
}) => {
  const [open, setOpen] = useState(false);

  // Which set is unfolded. The first one to begin with: a sheet that opens as
  // two shut bars looks like it is still loading, and the set that leads is
  // the one the screen narrows by first.
  const [expanded, setExpanded] = useState<string | null>(
    groups[0]?.label ?? null
  );

  const sectionId = React.useId();

  // Whether anything is actually narrowing the screen — see `defaultValue`.
  const filtered = groups.some(
    (group) =>
      group.defaultValue !== undefined && group.value !== group.defaultValue
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        // No box around it: the mark and the brand ink are what say this can be
        // pressed, and an outline at the end of a heading row draws more of the
        // eye than a filter that is not set to anything deserves. The padding
        // stays — it is the touch target, not the look.
        className={`flex flex-shrink-0 items-center gap-2 py-1.5 pl-1 text-[13px] font-medium text-brand-dark ${className}`}
      >
        {/* Three bars, each shorter than the one above it — a list being
            narrowed, which is the whole of what this opens. Drawn here rather
            than taken from the icon set, where the same three bars are centred
            and read as text alignment. */}
       
        Filter by
        {/* Not a word, because the word would be the whole state and there is
            no room for it. A dot says only "something is on", which is enough
            to send somebody into the sheet to find out what. */}
        {filtered && (
          <span
            aria-hidden="true"
            className="h-1.5 w-1.5 rounded-full bg-brand"
          />
        )}
         <svg
          aria-hidden="true"
          viewBox="0 0 16 12"
          className="h-3 w-4 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        >
          <path d="M1 2h14" />
          <path d="M1 6h9" />
          <path d="M1 10h5" />
        </svg>
      </button>

      <BottomSheet open={open} title={title} onClose={() => setOpen(false)}>
        <div className="px-5">
          {groups.map((group, index) => {
            const isExpanded = expanded === group.label;
            const listId = `${sectionId}-${index}`;
            const selected = group.options.find(
              (option) => option.value === group.value
            );

            return (
              <div
                key={group.label}
                className={index > 0 ? "border-t border-[#F1F4F6]" : ""}
              >
                <button
                  type="button"
                  aria-expanded={isExpanded}
                  aria-controls={listId}
                  // A second press folds it again, so the sheet can be put
                  // back the way it was found.
                  onClick={() => setExpanded(isExpanded ? null : group.label)}
                  className="flex w-full items-center justify-between gap-3 py-4 text-left"
                >
                  <span className="flex-shrink-0 text-[15px] font-semibold tracking-[-0.01em] text-ink">
                    {group.label}
                  </span>
                  <span className="flex min-w-0 items-center gap-2">
                    {/* What it is set to, so a folded set still reports itself
                        rather than being a row you have to open to read. It
                        carries the brand tint only while it is actually
                        narrowing something. */}
                    <span
                      className={`min-w-0 truncate rounded-full px-2.5 py-1 text-[12px] font-medium ${
                        group.defaultValue !== undefined &&
                        group.value !== group.defaultValue
                          ? "bg-brand-soft text-brand-dark"
                          : "bg-[#F3F6F8] text-ink-muted"
                      }`}
                    >
                      {selected?.label ?? "-"}
                    </span>
                    <ChevronDown
                      aria-hidden="true"
                      className={`h-4 w-4 flex-shrink-0 text-ink-subtle transition-transform duration-200 ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  </span>
                </button>

                {isExpanded && (
                  <div
                    id={listId}
                    role="listbox"
                    aria-label={group.label}
                    className="flex flex-wrap gap-2 pb-4 animate-unfold"
                  >
                    {group.options.map((option) => {
                      const isSelected = option.value === group.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          onClick={() => group.onChange(option.value)}
                          // Tall enough to be a thumb's target, and marked by
                          // its whole fill rather than by the colour of its
                          // word — a difference you have to look for is not a
                          // difference on a phone held at arm's length.
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[13px] transition-colors ${
                            isSelected
                              ? "border-brand bg-brand-soft font-medium text-brand-dark"
                              : "border-line bg-white text-ink"
                          }`}
                        >
                          {isSelected && (
                            <Check
                              aria-hidden="true"
                              className="h-3.5 w-3.5 flex-shrink-0"
                            />
                          )}
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </BottomSheet>
    </>
  );
};

export default FilterSheet;
